// Credit values have up to 4 decimal places.
const MAX_FRACTION_DIGITS = 4;

/**
 * Format an amount in the user's locale with the currency's symbol and usual decimal places, such
 * as "£1.50" or "¥150". Uses the narrow symbol ("$" rather than "US$"), since a gym only shows its
 * own currency.
 *
 * Pass `precise` to keep up to 4 decimal places beyond the usual ones. Without it, an amount that
 * would round to zero keeps them anyway, so a small nonzero value never shows as "£0.00".
 */
export default function formatCurrency(
  amount: number,
  currency: string,
  { precise = false }: { precise?: boolean } = {},
) {
  let standard: Intl.NumberFormat;
  try {
    standard = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
    });
  } catch {
    // Not a currency code Intl recognises.
    return `${amount.toLocaleString(undefined, { maximumFractionDigits: MAX_FRACTION_DIGITS })} ${currency}`;
  }
  const { minimumFractionDigits = 0, maximumFractionDigits = 2 } = standard.resolvedOptions();
  const isRoundedToZero = amount !== 0 && Number(amount.toFixed(maximumFractionDigits)) === 0;
  if (!precise && !isRoundedToZero) return standard.format(amount);

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits,
    maximumFractionDigits: Math.max(maximumFractionDigits, MAX_FRACTION_DIGITS),
  }).format(amount);
}
